import type { CSSProperties, ReactNode } from 'react';
import type { LogLevel } from '@pm2-monitor/shared';

/**
 * The design draws every surface with the same four declarations. They are
 * named here rather than repeated so a change to the panel treatment is one
 * edit, and so each view reads as layout instead of as styling.
 */
export const panelStyle: CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--line)',
  borderRadius: 16,
  boxShadow: 'var(--card)',
};

export const tabularNums: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};

export function Panel({
  children,
  style,
  ...rest
}: {
  children: ReactNode;
  style?: CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div style={{ ...panelStyle, padding: '22px 24px 18px', ...style }} {...rest}>
      {children}
    </div>
  );
}

export function Heading({
  children,
  size = 17,
  style,
}: {
  children: ReactNode;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 800,
        fontSize: size,
        letterSpacing: '-0.015em',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Sub({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4, ...style }}>
      {children}
    </div>
  );
}

/** The oversized readout used for every headline figure. */
export function Numeral({
  value,
  unit,
  size = 34,
  color,
}: {
  value: string;
  unit?: string;
  size?: number;
  color?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
      <div
        style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 800,
          fontSize: size,
          lineHeight: 1,
          letterSpacing: '-0.03em',
          ...(color ? { color } : {}),
          ...tabularNums,
        }}
      >
        {value}
      </div>
      {unit ? (
        <div
          style={{
            fontSize: Math.round(size * 0.56),
            color: 'var(--faint)',
            fontWeight: 600,
          }}
        >
          {unit}
        </div>
      ) : null}
    </div>
  );
}

export function Pill({
  children,
  bg = 'var(--page)',
  fg = 'var(--muted)',
  strong = false,
  style,
}: {
  children: ReactNode;
  bg?: string;
  fg?: string;
  strong?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      style={{
        padding: strong ? '3px 9px' : '3px 10px',
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: strong ? 11.5 : 11.5,
        fontWeight: strong ? 800 : 600,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/** A status dot with the design's tinted halo. */
export function Dot({
  color,
  ring,
  size = 7,
}: {
  color: string;
  ring: string;
  size?: number;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: 999,
        background: color,
        boxShadow: `0 0 0 ${Math.round(size * 0.45)}px ${ring}`,
      }}
    />
  );
}

/** `label ────── value`, the row shape used by Host, Connection and Runtime. */
export function DefRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '11px 0',
        borderTop: '1px solid var(--line-soft)',
        fontSize: 13.5,
      }}
    >
      <div style={{ color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontWeight: 600, textAlign: 'right', ...tabularNums }}>{value}</div>
    </div>
  );
}

/** Levels the bridge grades logs into, mapped to the design's chip palette. */
export function levelPill(level: LogLevel): { bg: string; fg: string } {
  if (level === 'error') return { bg: 'var(--neg-tint)', fg: 'var(--neg)' };
  if (level === 'warn') return { bg: 'var(--page)', fg: 'var(--ink)' };
  if (level === 'debug') return { bg: 'var(--page)', fg: 'var(--faint)' };
  return { bg: 'var(--pos-tint)', fg: 'var(--pos)' };
}

/** Empty and error states share one shape so the console never shows a blank panel. */
export function Placeholder({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '38px 24px',
        textAlign: 'center',
      }}
    >
      <Heading size={15}>{title}</Heading>
      {detail ? (
        <div
          style={{
            fontSize: 13,
            color: 'var(--muted)',
            maxWidth: '46ch',
            lineHeight: 1.55,
            textWrap: 'pretty',
          }}
        >
          {detail}
        </div>
      ) : null}
      {action}
    </div>
  );
}

/** Dark, high-contrast action button — the design's primary affordance. */
export function SolidButton({
  children,
  onClick,
  disabled,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 15px',
        borderRadius: 999,
        background: 'var(--ink)',
        color: 'var(--panel)',
        fontSize: 13,
        fontWeight: 600,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function QuietButton({
  children,
  onClick,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      className="hoverable"
      onClick={onClick}
      style={{
        padding: '8px 15px',
        borderRadius: 999,
        border: '1px solid var(--line)',
        color: 'var(--muted)',
        fontSize: 13,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
